import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

export function SEOHead({
  title,
  description,
  image = "https://mane.dev/og.jpg",
  url = "https://mane.dev",
  type = "website",
  schema,
  noindex = false,
}: SEOHeadProps) {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update meta tags
    const updateMeta = (name: string, content: string) => {
      let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("name", name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    const updateProperty = (property: string, content: string) => {
      let element = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("property", property);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Core meta tags
    updateMeta("description", description);
    updateMeta("robots", noindex ? "noindex, nofollow" : "index, follow");

    // OG tags
    updateProperty("og:type", type);
    updateProperty("og:title", title);
    updateProperty("og:description", description);
    updateProperty("og:image", image);
    updateProperty("og:url", url);

    // Twitter Card
    updateMeta("twitter:card", "summary_large_image");
    updateMeta("twitter:title", title);
    updateMeta("twitter:description", description);
    updateMeta("twitter:image", image);

    // Canonical
    let canonical = document.querySelector("link[rel='canonical']") as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Schema markup
    const existingSchema = document.querySelector('script[type="application/ld+json"]:not([data-main])') as HTMLScriptElement | null;
    if (schema) {
      const schemaScript = existingSchema ?? document.createElement("script");
      if (!existingSchema) {
        schemaScript.setAttribute("type", "application/ld+json");
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = Array.isArray(schema)
        ? JSON.stringify(schema)
        : JSON.stringify(schema);
    } else if (existingSchema) {
      existingSchema.remove();
    }

    return () => {
      // Cleanup is optional - meta tags can persist
    };
  }, [title, description, image, url, type, schema, noindex]);

  return null;
}
